import React, { useMemo, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useDeviceUnlock } from '../context/DeviceUnlockContext';

const TabletUnlockGate: React.FC = () => {
  const { unlockWithPin, idleTimeoutMinutes } = useDeviceUnlock();
  const [pin, setPin] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const timeoutLabel = useMemo(() => {
    if (idleTimeoutMinutes % 60 === 0) {
      const hours = idleTimeoutMinutes / 60;
      return `${hours} hour${hours === 1 ? '' : 's'}`;
    }
    return `${idleTimeoutMinutes} minutes`;
  }, [idleTimeoutMinutes]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!/^\d{4}$/.test(pin)) {
      setError('Enter a valid 4-digit PIN.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const ok = await unlockWithPin(pin);
    setIsSubmitting(false);

    if (!ok) {
      setError('Incorrect PIN. Please try again.');
      setPin('');
      return;
    }

    setPin('');
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500/20 text-teal-300 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Tablet Unlock Required</h1>
            <p className="text-xs text-slate-400">Device inactive for over {timeoutLabel}</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4 mt-5">
          <label className="block">
            <span className="text-sm text-slate-300">Company PIN</span>
            <input
              autoFocus
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={pin}
              onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 4))}
              className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-center text-xl tracking-[0.45em] outline-none focus:border-teal-400"
              placeholder="••••"
              aria-label="Company unlock PIN"
            />
          </label>

          {error && <p className="text-sm text-rose-300">{error}</p>}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-lg bg-teal-500 hover:bg-teal-400 disabled:opacity-60 disabled:cursor-not-allowed text-slate-950 font-semibold py-2.5"
          >
            {isSubmitting ? 'Unlocking...' : 'Unlock Tablet'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TabletUnlockGate;
